import React, { useState, useEffect } from 'react';
import { Shield, Users, Activity, Settings, CheckCircle, XCircle, Mail, UserPlus, Edit2, Trash2, AlertCircle, Clock, Database, LogIn, RefreshCw, FileText, Eye, Download, Search, Filter, Truck, ClipboardCheck } from 'lucide-react';
import { useAuth, useRole } from '../context/AuthContext';
import { AuthService } from '../services/authService';
import { supabase } from '../lib/supabaseClient';
import { ActivityLogger } from '../services/activityLogger';

type TabType = 'users' | 'activity' | 'settings';

// Interfejs dla użytkownika - obsługuje UUID i numery
interface User {
  id: number | string;  // Może być UUID z Supabase lub number z lokalnej bazy
  email: string;
  name: string;
  avatar_url?: string;
  role: string;
  modules_access: string[];
  is_active: boolean;
  last_login?: string;
  created_at: string;
}

// Interfejs dla oferty
interface Offer {
  id: number;
  offer_number: string;
  client_name: string;
  client_nip: string;
  salesperson_name: string;
  total_net: number;
  status: string;
  created_at: string;
  updated_at: string;
}

// Lista dostępnych modułów - zaktualizowana zgodnie z AppRoutes.tsx
const AVAILABLE_MODULES = [
  { value: 'dashboard', label: 'Dashboard', icon: '📊' },
  { value: 'calculator', label: 'Kalkulator PRO', icon: '🧮' },
  { value: 'offers', label: 'Oferty', icon: '📄' },
  { value: 'clients', label: 'Klienci', icon: '👥' },
  { value: 'production', label: 'Produkcja', icon: '🏭' },
  { value: 'machining', label: 'Parametry obróbki', icon: '⚙️' },
  { value: 'quality', label: 'Kontrola Jakości', icon: '📋' },
  { value: 'profitability', label: 'Rentowność', icon: '💹' },
  { value: 'reports', label: 'Raporty', icon: '📊' },
  { value: 'ai', label: 'AI Assistant', icon: '🤖' },
  { value: 'social-media', label: 'Social Media', icon: '📢' },
  { value: 'google-ads', label: 'Google Ads', icon: '🎯' },
  { value: 'chatbot-settings', label: 'Ustawienia Chatbota', icon: '💬' },
  { value: 'chatbot-analytics', label: 'Analityka Chatbota', icon: '📈' },
  { value: 'automation', label: 'Automatyzacja', icon: '⚡' },
  { value: 'notifications', label: 'Powiadomienia', icon: '🔔' },
  { value: 'configurator3d', label: 'Konfigurator 3D', icon: '📦' },
  { value: 'visualizer', label: 'Wizualizer', icon: '👁️' },
  { value: 'expositor-designer', label: 'Projektant ekspozytorów', icon: '🏗️' },
  { value: 'knowledge', label: 'Baza wiedzy', icon: '📚' },
  { value: 'marketplace', label: 'Marketplace', icon: '🛒' },
  { value: 'materials-database', label: 'Baza materiałów', icon: '🗂️' },
  { value: 'technical', label: 'Narzędzia techniczne', icon: '🔧' },
  { value: 'payroll', label: 'Kadry/Płace', icon: '💰' },
  { value: 'settings', label: 'Ustawienia', icon: '⚙️' },
  { value: 'api-settings', label: 'Ustawienia API', icon: '🔌' },
  { value: 'admin', label: 'Panel Admin', icon: '🛡️' },
  { value: 'users', label: 'Zarządzanie użytkownikami', icon: '👥' },
  { value: 'system-health', label: 'Stan systemu', icon: '🏥' }
];

// Lista predefiniowanych ról
const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Pełny dostęp do systemu' },
  { value: 'manager', label: 'Kierownik', description: 'Zarządzanie produkcją i zespołem' },
  { value: 'salesperson', label: 'Handlowiec', description: 'Tworzenie ofert i kontakt z klientami' },
  { value: 'operator', label: 'Operator', description: 'Obsługa produkcji' },
  { value: 'warehouse', label: 'Magazynier', description: 'Zarządzanie magazynem' },
  { value: 'viewer', label: 'Podgląd', description: 'Tylko odczyt' }
];

const AdminPanel: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [useBackend, setUseBackend] = useState(true); // Domyślnie używaj backend API
  const [isOfferManagementOpen, setIsOfferManagementOpen] = useState(false);
  const [isWZManagementOpen, setIsWZManagementOpen] = useState(false);
  const [isQCManagementOpen, setIsQCManagementOpen] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [filteredOffers, setFilteredOffers] = useState<Offer[]>([]);
  const [wzDocuments, setWzDocuments] = useState<any[]>([]);
  const [qcChecks, setQcChecks] = useState<any[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [wzLoading, setWzLoading] = useState(false);
  const [qcLoading, setQcLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Funkcje pomocnicze dla zarządzania danymi
  const handleClearLogs = () => {
    if (confirm('Czy na pewno chcesz wyczyścić wszystkie logi aktywności?')) {
      const clearedLogs = activityLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return logDate > thirtyDaysAgo;
      });
      setActivityLogs(clearedLogs);
      localStorage.setItem('activityLogs', JSON.stringify(clearedLogs));
      alert('Logi starsze niż 30 dni zostały usunięte');
    }
  };

  const handleResetSettings = () => {
    if (confirm('Czy na pewno chcesz zresetować wszystkie ustawienia lokalne?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  // Funkcje do zarządzania ofertami
  const fetchOffers = async () => {
    setOffersLoading(true);
    try {
      // Pobierz oferty z Supabase
      const { data: offersData, error: offersError } = await supabase
        .from('offers')
        .select('*')
        .order('created_at', { ascending: false });

      if (offersError) {
        console.error('Błąd podczas pobierania ofert:', offersError);
        throw offersError;
      }

      // Pobierz klientów osobno
      const clientIds = [...new Set(offersData?.map(o => o.client_id).filter(Boolean) || [])];
      let clientsMap = new Map();
      
      if (clientIds.length > 0) {
        const { data: clientsData } = await supabase
          .from('clients')
          .select('id, name, nip')
          .in('id', clientIds);
        
        if (clientsData) {
          clientsData.forEach(client => {
            clientsMap.set(client.id, client);
          });
        }
      }

      const data = offersData;
      const error = offersError;

      if (error) {
        console.error('Błąd podczas pobierania ofert z Supabase:', error);
        // Fallback do localStorage
        const storedOffers = localStorage.getItem('offers');
        if (storedOffers) {
          const parsedOffers = JSON.parse(storedOffers);
          setOffers(parsedOffers);
          setFilteredOffers(parsedOffers);
        }
      } else if (data) {
        // Mapuj dane do struktury Offer
        const mappedOffers: Offer[] = data.map(item => {
          const client = clientsMap.get(item.client_id);
          return {
            id: item.id,
            offer_number: item.offer_number,
            client_name: client?.name || 'Nieznany klient',
            client_nip: client?.nip || '',
            salesperson_name: item.salesperson_name || '',
            total_net: item.total_net || 0,
            status: item.status || 'draft',
            created_at: item.created_at,
            updated_at: item.updated_at
          };
        });
        setOffers(mappedOffers);
        setFilteredOffers(mappedOffers);
      }
    } catch (error) {
      console.error('Błąd podczas pobierania ofert:', error);
      setOffers([]);
      setFilteredOffers([]);
    } finally {
      setOffersLoading(false);
    }
  };

  const deleteOffer = async (offerId: number) => {
    if (confirm('Czy na pewno chcesz usunąć tę ofertę? Ta operacja jest nieodwracalna.')) {
      try {
        // Usuń z Supabase
        const { error } = await supabase
          .from('offers')
          .delete()
          .eq('id', offerId);

        if (error) {
          console.error('Błąd podczas usuwania oferty z Supabase:', error);
          alert('Wystąpił błąd podczas usuwania oferty z bazy danych');
        } else {
          // Aktualizuj lokalny stan
          const updatedOffers = offers.filter(offer => offer.id !== offerId);
          setOffers(updatedOffers);
          setFilteredOffers(updatedOffers.filter(offer => 
            filterOffer(offer, searchTerm, statusFilter)
          ));
          
          // Loguj akcję
          await ActivityLogger.logDelete('oferta', { offerId, offerNumber: offers.find(o => o.id === offerId)?.offer_number });
          
          alert('Oferta została pomyślnie usunięta');
        }
      } catch (error) {
        console.error('Błąd podczas usuwania oferty:', error);
        alert('Wystąpił błąd podczas usuwania oferty');
      }
    }
  };

  // Funkcje do zarządzania dokumentami WZ
  const fetchWZDocuments = async () => {
    setWzLoading(true);
    try {
      const { data, error } = await supabase
        .from('shipping_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Błąd podczas pobierania dokumentów WZ:', error);
      } else {
        setWzDocuments(data || []);
      }
    } catch (error) {
      console.error('Błąd podczas pobierania dokumentów WZ:', error);
      setWzDocuments([]);
    } finally {
      setWzLoading(false);
    }
  };

  const deleteWZDocument = async (docId: string) => {
    if (confirm('Czy na pewno chcesz usunąć ten dokument WZ? Ta operacja jest nieodwracalna.')) {
      try {
        const { error } = await supabase
          .from('shipping_documents')
          .delete()
          .eq('id', docId);

        if (error) {
          console.error('Błąd podczas usuwania dokumentu WZ:', error);
          alert('Wystąpił błąd podczas usuwania dokumentu WZ');
        } else {
          setWzDocuments(wzDocuments.filter(doc => doc.id !== docId));
          alert('Dokument WZ został pomyślnie usunięty');
        }
      } catch (error) {
        console.error('Błąd podczas usuwania dokumentu WZ:', error);
        alert('Wystąpił błąd podczas usuwania dokumentu WZ');
      }
    }
  };

  // Funkcje do zarządzania kontrolami jakości
  const fetchQCChecks = async () => {
    setQcLoading(true);
    try {
      const { data, error } = await supabase
        .from('quality_checks')
        .select('*')
        .order('check_date', { ascending: false });

      if (error) {
        console.error('Błąd podczas pobierania kontroli jakości:', error);
      } else {
        setQcChecks(data || []);
      }
    } catch (error) {
      console.error('Błąd podczas pobierania kontroli jakości:', error);
      setQcChecks([]);
    } finally {
      setQcLoading(false);
    }
  };

  const deleteQCCheck = async (checkId: string) => {
    if (confirm('Czy na pewno chcesz usunąć tę kontrolę jakości? Ta operacja jest nieodwracalna.')) {
      try {
        // Najpierw usuń powiązane pomiary i defekty
        await supabase.from('quality_measurements').delete().eq('quality_check_id', checkId);
        await supabase.from('quality_defects').delete().eq('quality_check_id', checkId);
        
        // Następnie usuń główny rekord
        const { error } = await supabase
          .from('quality_checks')
          .delete()
          .eq('id', checkId);

        if (error) {
          console.error('Błąd podczas usuwania kontroli jakości:', error);
          alert('Wystąpił błąd podczas usuwania kontroli jakości');
        } else {
          setQcChecks(qcChecks.filter(check => check.id !== checkId));
          alert('Kontrola jakości została pomyślnie usunięta');
        }
      } catch (error) {
        console.error('Błąd podczas usuwania kontroli jakości:', error);
        alert('Wystąpił błąd podczas usuwania kontroli jakości');
      }
    }
  };

  // Funkcja pomocnicza do filtrowania ofert
  const filterOffer = (offer: Offer, search: string, status: string) => {
    const matchesSearch = search === '' || 
      offer.offer_number.toLowerCase().includes(search.toLowerCase()) ||
      offer.client_name.toLowerCase().includes(search.toLowerCase()) ||
      offer.client_nip.includes(search) ||
      offer.salesperson_name.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = status === 'all' || offer.status === status;
    
    return matchesSearch && matchesStatus;
  };

  // Stan formularza
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer',
    modules_access: [] as string[]
  });

  // Jeśli nie jest adminem, nie powinien tu być
  if (!isAdmin()) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-6 text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-500 mb-2">Brak dostępu</h2>
          <p className="text-gray-400">Tylko administratorzy mają dostęp do tego panelu.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [activeTab, useBackend]);
  
  // Loguj otwarcie modułu przy pierwszym renderze
  useEffect(() => {
    ActivityLogger.logModuleAccess('Panel Administratora');
  }, []);

  // Pobierz oferty gdy modal się otwiera
  useEffect(() => {
    if (isOfferManagementOpen) {
      fetchOffers();
    }
  }, [isOfferManagementOpen]);

  // Pobierz dokumenty WZ gdy modal się otwiera
  useEffect(() => {
    if (isWZManagementOpen) {
      fetchWZDocuments();
    }
  }, [isWZManagementOpen]);

  // Pobierz kontrole jakości gdy modal się otwiera
  useEffect(() => {
    if (isQCManagementOpen) {
      fetchQCChecks();
    }
  }, [isQCManagementOpen]);

  // Filtruj oferty gdy zmienia się wyszukiwanie lub status
  useEffect(() => {
    const filtered = offers.filter(offer => 
      filterOffer(offer, searchTerm, statusFilter)
    );
    setFilteredOffers(filtered);
  }, [offers, searchTerm, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // 1. ZAWSZE najpierw Supabase (główne źródło prawdy)
      try {
        const { data: supabaseUsers, error } = await supabase
          .from('users')
          .select('*')
          .order('created_at', { ascending: false });

        if (!error && supabaseUsers && supabaseUsers.length > 0) {
          // Mapuj dane z Supabase na format lokalny
          const mappedUsers: User[] = supabaseUsers.map(user => ({
            id: user.id || Date.now(),
            email: user.email,
            name: user.full_name || user.email?.split('@')[0] || 'Użytkownik',
            role: user.role || 'viewer',
            modules_access: user.modules_access || [],
            is_active: user.is_active !== false,
            last_login: user.last_login,
            created_at: user.created_at,
            avatar_url: user.avatar_url
          }));
          
          console.log('✅ Pobrano użytkowników z Supabase:', mappedUsers.length);
          setUsers(mappedUsers);
          
          // Zapisz lokalnie jako backup
          localStorage.setItem('plexisystem_users', JSON.stringify(mappedUsers));
          setLoading(false);
          return;
        } else if (error) {
          console.error('⚠️ Błąd Supabase:', error.message);
        }
      } catch (supabaseError) {
        console.error('⚠️ Błąd połączenia z Supabase:', supabaseError);
      }

      // 2. Backend API jako drugie źródło
      try {
        const response = await AuthService.fetchWithAuth('/api/users');
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Pobrano użytkowników z backend API:', data.length);
          
          // Mapuj i zapisz
          const mappedUsers = data.map((user: any) => ({
            ...user,
            modules_access: Array.isArray(user.modules_access) ? user.modules_access : JSON.parse(user.modules_access || '[]'),
            is_active: !!user.is_active
          }));
          
          setUsers(mappedUsers);
          localStorage.setItem('plexisystem_users', JSON.stringify(mappedUsers));
          setLoading(false);
          return;
        } else {
          console.error('⚠️ Backend API status:', response.status);
        }
      } catch (error) {
        console.error('⚠️ Błąd backend API:', error);
      }
      
      // 3. Ostatni fallback - localStorage
      const savedUsers = localStorage.getItem('plexisystem_users');
      if (savedUsers) {
        const localUsers = JSON.parse(savedUsers);
        console.log('📦 Używam cache lokalnego:', localUsers.length, 'użytkowników');
        setUsers(localUsers);
      } else {
        console.log('ℹ️ Brak użytkowników - pusta lista');
        setUsers([]);
      }
    } catch (error) {
      console.error('❌ Błąd krytyczny:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      // ZAWSZE próbuj najpierw pobrać prawdziwe logi z backend
      try {
        const response = await AuthService.fetchWithAuth('/api/users/activity-logs');
        if (response.ok) {
          const logs = await response.json();
          console.log('✅ Pobrano logi aktywności z backend:', logs.length);
          setActivityLogs(logs);
          return;
        } else {
          console.log('⚠️ Backend nie zwrócił logów, status:', response.status);
        }
      } catch (backendError) {
        console.error('❌ Błąd połączenia z backend dla logów:', backendError);
      }
      
      // Jeśli backend nie działa, pobierz z localStorage historię akcji
      const storedLogs = localStorage.getItem('plexisystem_activity_logs');
      if (storedLogs) {
        const logs = JSON.parse(storedLogs);
        console.log('📦 Używam lokalnych logów:', logs.length);
        setActivityLogs(logs);
      } else {
        // Brak logów - ustaw pustą tablicę
        console.log('ℹ️ Brak logów aktywności');
        setActivityLogs([]);
      }
    } catch (error) {
      console.error('❌ Błąd pobierania logów:', error);
      setActivityLogs([]);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      name: '',
      role: 'viewer',
      modules_access: []
    });
    setIsDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      modules_access: user.modules_access
    });
    setIsDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!formData.email) {
      alert('Email jest wymagany!');
      return;
    }

    setSaveLoading(true);
    try {
      // NAJPIERW zapisz do Supabase (główna baza danych)
      try {
        if (editingUser) {
          // Aktualizacja istniejącego użytkownika w Supabase
          const { error } = await supabase
            .from('users')
            .update({
              full_name: formData.name || formData.email.split('@')[0],
              role: formData.role,
              modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard'],
              updated_at: new Date().toISOString()
            })
            .eq('email', editingUser.email);

          if (error) {
            console.error('❌ Błąd aktualizacji w Supabase:', error);
            // Nie przerywaj - spróbuj zapisać lokalnie
          } else {
            console.log('✅ Użytkownik zaktualizowany w Supabase');
          }
        } else {
          // Sprawdź czy użytkownik już istnieje
          const { data: existingUser, error: checkError } = await supabase
            .from('users')
            .select('*')
            .eq('email', formData.email)
            .maybeSingle(); // Użyj maybeSingle zamiast single żeby uniknąć błędu gdy nie ma rekordu

          if (existingUser) {
            // Użytkownik istnieje - zaktualizuj
            const { error } = await supabase
              .from('users')
              .update({
                full_name: formData.name || formData.email.split('@')[0],
                role: formData.role,
                modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard'],
                updated_at: new Date().toISOString()
              })
              .eq('email', formData.email);

            if (error) {
              console.error('❌ Błąd aktualizacji w Supabase:', error);
            } else {
              console.log('✅ Użytkownik zaktualizowany w Supabase');
            }
          } else {
            // Dodaj nowego użytkownika do Supabase
            const { data: insertData, error } = await supabase
              .from('users')
              .insert([
                {
                  email: formData.email,
                  full_name: formData.name || formData.email.split('@')[0],
                  role: formData.role,
                  modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard'],
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                }
              ])
              .select();

            if (error) {
              console.error('❌ Błąd dodawania do Supabase:', error);
              console.error('Error details:', error.message, error.details, error.hint);
              
              // Jeśli błąd RLS, pokaż informację
              if (error.code === '42501' || error.message?.includes('row-level security')) {
                console.warn('⚠️ RLS blokuje dodawanie. Użytkownik zostanie zapisany tylko lokalnie i w backend.');
                alert('⚠️ Uwaga: Nie można zapisać w głównej bazie danych (RLS). Użytkownik zapisany lokalnie.\n\nSkontaktuj się z administratorem żeby wykonać skrypt SQL.');
              }
            } else {
              console.log('✅ Użytkownik dodany do Supabase', insertData);
            }
          }
        }
      } catch (supabaseError) {
        console.error('❌ Błąd połączenia z Supabase:', supabaseError);
      }

      // Próba zapisu do backend (jeśli włączony)
      if (useBackend) {
        try {
          const endpoint = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
          const method = editingUser ? 'PUT' : 'POST';
          
          const response = await AuthService.fetchWithAuth(endpoint, {
            method,
            body: JSON.stringify({
              email: formData.email,
              name: formData.name || formData.email.split('@')[0],
              role: formData.role,
              modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard']
            })
          });
          
          if (response.ok) {
            console.log('✅ Użytkownik zapisany w backend');
          } else {
            const error = await response.json();
            console.error('❌ Błąd zapisu w backend:', error);
          }
        } catch (error) {
          console.error('❌ Błąd połączenia z backend:', error);
        }
      }

      // Zapis lokalny (zawsze jako backup)
      const currentUsers = [...users];
      
      if (editingUser) {
        // Edycja istniejącego użytkownika
        const index = currentUsers.findIndex(u => u.id === editingUser.id);
        if (index !== -1) {
          currentUsers[index] = {
            ...currentUsers[index],
            ...formData,
            modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard']
          };
        }
      } else {
        // Dodawanie nowego użytkownika
        const newUser: User = {
          id: Date.now(), // Tymczasowe ID
          email: formData.email,
          name: formData.name || formData.email.split('@')[0],
          role: formData.role,
          modules_access: formData.modules_access.length > 0 ? formData.modules_access : ['dashboard'],
          is_active: true,
          last_login: undefined,
          created_at: new Date().toISOString(),
          avatar_url: undefined
        };
        currentUsers.push(newUser);
      }
      
      // Zapisz do localStorage
      localStorage.setItem('plexisystem_users', JSON.stringify(currentUsers));
      setUsers(currentUsers);
      
      console.log('✅ Użytkownik zapisany we wszystkich lokalizacjach');
      
      // Loguj akcję
      if (editingUser) {
        await ActivityLogger.logUpdate('użytkownik', { email: formData.email, role: formData.role });
      } else {
        await ActivityLogger.logCreate('użytkownik', { email: formData.email, role: formData.role });
      }
      
      alert(`Użytkownik ${formData.email} został pomyślnie ${editingUser ? 'zaktualizowany' : 'dodany'}!\n\nPrzypisane moduły: ${formData.modules_access.join(', ') || 'brak'}`);
      setIsDialogOpen(false);
      
      // Odśwież listę użytkowników
      fetchUsers();
    } catch (error) {
      console.error('Błąd zapisywania użytkownika:', error);
      alert('Wystąpił błąd podczas zapisywania użytkownika');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number | string) => {
    if (!confirm('Czy na pewno chcesz usunąć tego użytkownika?')) return;
    
    try {
      // Nie pozwól usunąć administratora
      const userToDelete = users.find(u => u.id === userId || String(u.id) === String(userId));
      if (userToDelete?.email === 'lukasz.sikorra@gmail.com') {
        alert('Nie możesz usunąć głównego administratora!');
        return;
      }
      
      // 1. Najpierw próbuj usunąć z Supabase (główne źródło prawdy)
      if (userToDelete?.email) {
        try {
          const { error } = await supabase
            .from('users')
            .delete()
            .eq('email', userToDelete.email);
          
          if (!error) {
            console.log('✅ Użytkownik usunięty z Supabase');
            // Odśwież listę i zakończ
            await fetchUsers();
            alert('Użytkownik został pomyślnie usunięty');
            return;
          } else {
            console.error('⚠️ Błąd usuwania z Supabase:', error);
            // Kontynuuj z backend API
          }
        } catch (supabaseError) {
          console.error('⚠️ Błąd połączenia z Supabase:', supabaseError);
        }
      }
      
      // 2. Próba usunięcia z backend API
      if (useBackend) {
        try {
          const response = await AuthService.fetchWithAuth(`/api/users/${userId}`, {
            method: 'DELETE'
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('✅ Użytkownik usunięty z backend:', result);
            
            // Jeśli to był UUID user (soft delete)
            if (result.method === 'soft_delete' || result.method === 'supabase') {
              // Usuń także lokalnie
              const updatedUsers = users.filter(u => u.id !== userId && String(u.id) !== String(userId));
              localStorage.setItem('plexisystem_users', JSON.stringify(updatedUsers));
              setUsers(updatedUsers);
            }
            
            await fetchUsers();
            alert('Użytkownik został pomyślnie usunięty');
            return;
          } else {
            console.error('❌ Błąd usuwania z backend:', response.status);
          }
        } catch (error) {
          console.error('❌ Błąd połączenia z backend:', error);
        }
      }
      
      // 3. Ostatni fallback - usuwanie lokalne
      const updatedUsers = users.filter(u => u.id !== userId && String(u.id) !== String(userId));
      localStorage.setItem('plexisystem_users', JSON.stringify(updatedUsers));
      setUsers(updatedUsers);
      
      // Loguj akcję
      await ActivityLogger.logDelete('użytkownik', { userId, email: userToDelete?.email });
      
      console.log('✅ Użytkownik usunięty lokalnie');
      alert('Użytkownik został usunięty lokalnie (tylko w tej przeglądarce)');
    } catch (error) {
      console.error('Błąd usuwania użytkownika:', error);
      alert('Wystąpił błąd podczas usuwania użytkownika');
    }
  };

  const toggleModule = (moduleValue: string) => {
    setFormData(prev => ({
      ...prev,
      modules_access: prev.modules_access.includes(moduleValue)
        ? prev.modules_access.filter(m => m !== moduleValue)
        : [...prev.modules_access, moduleValue]
    }));
  };



  const tabs = [
    { id: 'users' as TabType, label: 'Użytkownicy', icon: Users },
    { id: 'activity' as TabType, label: 'Aktywność', icon: Activity },
    { id: 'settings' as TabType, label: 'Ustawienia', icon: Settings }
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Panel Administratora</h1>
        <p className="text-gray-400">Zarządzaj użytkownikami i monitoruj aktywność systemu</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Użytkownicy */}
        {activeTab === 'users' && (
          <div>
            <div className="space-y-4 mb-6">
              <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-400 font-medium">System autoryzacji Google OAuth</p>
                    <p className="text-blue-300 text-sm mt-1">
                      Dodaj email pracownika poniżej. Przy pierwszym logowaniu przez Google, 
                      konto zostanie automatycznie utworzone z przypisanymi uprawnieniami.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Database className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">Źródło danych</p>
                      <p className="text-gray-400 text-sm">
                        Supabase + Backend API
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      fetchUsers();
                      fetchActivityLogs();
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span className="text-sm">
                      Odśwież dane
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">Zarządzanie użytkownikami</h2>
              <button
                onClick={handleAddUser}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Dodaj użytkownika
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Użytkownik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Rola
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Moduły
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Ostatnie logowanie
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-750">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt={user.name} className="h-8 w-8 rounded-full" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                                <Mail className="h-4 w-4 text-orange-500" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-white">{user.name}</div>
                              <div className="text-sm text-gray-400">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs rounded-full bg-orange-500/20 text-orange-400">
                            {ROLES.find(r => r.value === user.role)?.label || user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {user.modules_access.includes('wszystkie') ? (
                              <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">
                                Wszystkie moduły
                              </span>
                            ) : (
                              user.modules_access.slice(0, 3).map((module) => (
                                <span key={module} className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                                  {AVAILABLE_MODULES.find(m => m.value === module)?.label || module}
                                </span>
                              ))
                            )}
                            {user.modules_access.length > 3 && !user.modules_access.includes('wszystkie') && (
                              <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                                +{user.modules_access.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {user.is_active ? (
                              <>
                                <CheckCircle className="h-4 w-4 text-green-400" />
                                <span className="text-sm text-green-400">Aktywny</span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 text-red-400" />
                                <span className="text-sm text-red-400">Nieaktywny</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {user.last_login ? new Date(user.last_login).toLocaleString('pl-PL') : 'Nigdy'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-gray-400 hover:text-white"
                              title="Edytuj"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Usuń"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Aktywność */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {/* Mini Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {/* Widget: Aktywni użytkownicy */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Aktywni dziś</p>
                    <p className="text-3xl font-bold mt-1">
                      {users.filter(u => {
                        if (!u.last_login) return false;
                        const lastLogin = new Date(u.last_login);
                        const today = new Date();
                        return lastLogin.toDateString() === today.toDateString();
                      }).length}
                    </p>
                  </div>
                  <Users className="h-10 w-10 text-orange-200" />
                </div>
                <p className="text-orange-100 text-xs mt-2">
                  z {users.length} wszystkich
                </p>
              </div>

              {/* Widget: Ostatnie logowanie */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Ostatnie logowanie</p>
                    <p className="text-lg font-semibold mt-1 truncate">
                      {activityLogs.find(log => log.action.includes('Zalogowano'))?.user || 'Brak danych'}
                    </p>
                  </div>
                  <LogIn className="h-10 w-10 text-blue-200" />
                </div>
                <p className="text-blue-100 text-xs mt-2">
                  {activityLogs.find(log => log.action.includes('Zalogowano'))
                    ? new Date(activityLogs.find(log => log.action.includes('Zalogowano')).timestamp).toLocaleString('pl-PL')
                    : 'Brak danych'}
                </p>
              </div>

              {/* Widget: Aktywność 24h */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Akcje dziś</p>
                    <p className="text-3xl font-bold mt-1">
                      {activityLogs.filter(log => {
                        const logDate = new Date(log.timestamp);
                        const now = new Date();
                        return (now.getTime() - logDate.getTime()) < 24 * 60 * 60 * 1000;
                      }).length}
                    </p>
                  </div>
                  <Activity className="h-10 w-10 text-green-200" />
                </div>
                <p className="text-green-100 text-xs mt-2">
                  ostatnie 24 godziny
                </p>
              </div>
            </div>

            {/* Prosty wykres aktywności */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Aktywność w czasie</h3>
              <div className="h-40 flex items-end gap-2">
                {/* Generuj słupki dla ostatnich 7 dni */}
                {[...Array(7)].map((_, index) => {
                  const date = new Date();
                  date.setDate(date.getDate() - (6 - index));
                  const dayLogs = activityLogs.filter(log => {
                    const logDate = new Date(log.timestamp);
                    return logDate.toDateString() === date.toDateString();
                  });
                  const height = Math.max(10, (dayLogs.length / Math.max(...[...Array(7)].map((_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() - (6 - i));
                    return activityLogs.filter(l => new Date(l.timestamp).toDateString() === d.toDateString()).length;
                  })) * 100));
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className="w-full bg-orange-500 rounded-t transition-all duration-500 hover:bg-orange-400"
                        style={{ height: `${height}%` }}
                        title={`${dayLogs.length} akcji`}
                      />
                      <span className="text-xs text-gray-400">
                        {date.toLocaleDateString('pl-PL', { weekday: 'short' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tabela logów */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Szczegółowe logi</h2>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="h-4 w-4" />
                  <span>Ostatnie 24 godziny</span>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Czas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Użytkownik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Akcja
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Moduł
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        IP
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {activityLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-750">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {new Date(log.timestamp).toLocaleString('pl-PL')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                          {log.user}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {log.action}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
                            {log.module}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {log.ip}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Ustawienia */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Zarządzanie danymi */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-orange-400">Zarządzanie danymi</h3>
              <div className="space-y-4">
                {/* Usuwanie ofert */}
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Zarządzanie ofertami</p>
                    <p className="text-sm text-gray-400">Przeglądaj i usuwaj oferty z systemu</p>
                  </div>
                  <button
                    onClick={() => setIsOfferManagementOpen(true)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Zarządzaj ofertami
                  </button>
                </div>
                
                {/* Usuwanie dokumentów WZ */}
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Zarządzanie dokumentami WZ</p>
                    <p className="text-sm text-gray-400">Przeglądaj i usuwaj dokumenty wydania</p>
                  </div>
                  <button
                    onClick={() => setIsWZManagementOpen(true)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <Truck className="h-4 w-4" />
                    Zarządzaj WZ
                  </button>
                </div>
                
                {/* Usuwanie kontroli jakości */}
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Zarządzanie kontrolami jakości</p>
                    <p className="text-sm text-gray-400">Przeglądaj i usuwaj kontrole jakości</p>
                  </div>
                  <button
                    onClick={() => setIsQCManagementOpen(true)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    Zarządzaj QC
                  </button>
                </div>
                
                {/* Czyszczenie logów */}
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Czyszczenie logów aktywności</p>
                    <p className="text-sm text-gray-400">Usuń stare logi aktywności użytkowników</p>
                  </div>
                  <button
                    onClick={handleClearLogs}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Wyczyść logi
                  </button>
                </div>
                
                {/* Reset ustawień */}
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Reset ustawień lokalnych</p>
                    <p className="text-sm text-gray-400">Przywróć domyślne ustawienia przeglądarki</p>
                  </div>
                  <button
                    onClick={handleResetSettings}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Resetuj
                  </button>
                </div>
              </div>
            </div>
            
            {/* Informacje o systemie */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-orange-400">Informacje o systemie</h3>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <dt className="text-sm text-gray-400">Wersja aplikacji</dt>
                  <dd className="text-lg font-semibold">2.1.0</dd>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <dt className="text-sm text-gray-400">Backend</dt>
                  <dd className="text-lg font-semibold">plexisystem-backend.onrender.com</dd>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <dt className="text-sm text-gray-400">Typ autoryzacji</dt>
                  <dd className="text-lg font-semibold">Google OAuth 2.0</dd>
                </div>
                <div className="bg-gray-700 rounded-lg p-4">
                  <dt className="text-sm text-gray-400">Środowisko</dt>
                  <dd className="text-lg font-semibold">Produkcja</dd>
                </div>
              </dl>
            </div>

            {/* Statystyki */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-orange-400">Statystyki systemu</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <Users className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{users.length}</p>
                  <p className="text-sm text-gray-400">Aktywnych użytkowników</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <LogIn className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{activityLogs.filter(log => log.action.includes('Zalogowano')).length}</p>
                  <p className="text-sm text-gray-400">Logowań dzisiaj</p>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <Database className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold">{useBackend ? 'SQLite (Render)' : 'LocalStorage'}</p>
                  <p className="text-sm text-gray-400">Baza danych</p>
                </div>
              </div>
            </div>

            {/* Konfiguracja systemu */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-orange-400">Konfiguracja systemu</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium">Automatyczne wylogowanie</p>
                    <p className="text-sm text-gray-400">Wyloguj nieaktywnych użytkowników po 24h</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium">Logowanie aktywności</p>
                    <p className="text-sm text-gray-400">Zapisuj wszystkie akcje użytkowników</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="font-medium">Powiadomienia email</p>
                    <p className="text-sm text-gray-400">Wysyłaj powiadomienia o nowych użytkownikach</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dialog zarządzania ofertami */}
      {isOfferManagementOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-75" onClick={() => setIsOfferManagementOpen(false)} />
            
            <div className="relative bg-gray-800 rounded-lg max-w-full lg:max-w-5xl w-full p-4 md:p-6 max-h-[85vh] overflow-auto">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Zarządzanie ofertami</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Znaleziono {filteredOffers.length} z {offers.length} ofert
                  </p>
                </div>
                <button
                  onClick={() => setIsOfferManagementOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* Filtry i wyszukiwarka */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Szukaj po numerze, kliencie, NIP lub handlowcu..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  >
                    <option value="all">Wszystkie statusy</option>
                    <option value="pending">Oczekujące</option>
                    <option value="accepted">Zaakceptowane</option>
                    <option value="rejected">Odrzucone</option>
                    <option value="draft">Szkice</option>
                  </select>
                  <button
                    onClick={fetchOffers}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Odśwież
                  </button>
                </div>
              </div>

              {offersLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : filteredOffers.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Nie znaleziono ofert spełniających kryteria wyszukiwania'
                      : 'Brak ofert w systemie'}
                  </p>
                  {(searchTerm || statusFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                      }}
                      className="mt-4 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Wyczyść filtry
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Numer oferty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Klient
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          NIP
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Handlowiec
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Wartość netto
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Data utworzenia
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Akcje
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {filteredOffers.map((offer) => (
                        <tr key={offer.id} className="hover:bg-gray-800">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                            {offer.offer_number}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                            {offer.client_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                            {offer.client_nip}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                            {offer.salesperson_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-white">
                            {offer.total_net.toLocaleString('pl-PL', { 
                              style: 'currency', 
                              currency: 'PLN' 
                            })}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              offer.status === 'accepted' 
                                ? 'bg-green-500/20 text-green-400'
                                : offer.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : offer.status === 'rejected'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {offer.status === 'accepted' ? 'Zaakceptowana' :
                               offer.status === 'pending' ? 'Oczekująca' :
                               offer.status === 'rejected' ? 'Odrzucona' : offer.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-400">
                            {new Date(offer.created_at).toLocaleDateString('pl-PL')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => {
                                  // TODO: Dodać podgląd oferty
                                  alert('Funkcja podglądu będzie dostępna wkrótce');
                                }}
                                className="text-blue-400 hover:text-blue-300"
                                title="Podgląd"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  // TODO: Dodać eksport PDF
                                  alert('Funkcja eksportu będzie dostępna wkrótce');
                                }}
                                className="text-green-400 hover:text-green-300"
                                title="Eksportuj PDF"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => deleteOffer(offer.id)}
                                className="text-red-400 hover:text-red-300"
                                title="Usuń"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsOfferManagementOpen(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog zarządzania dokumentami WZ */}
      {isWZManagementOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-75" onClick={() => setIsWZManagementOpen(false)} />
            
            <div className="relative bg-gray-800 rounded-lg max-w-full lg:max-w-5xl w-full p-4 md:p-6 max-h-[85vh] overflow-auto">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Zarządzanie dokumentami WZ</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Znaleziono {wzDocuments.length} dokumentów
                  </p>
                </div>
                <button
                  onClick={() => setIsWZManagementOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {wzLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : wzDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Brak dokumentów WZ w systemie</p>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Numer dokumentu</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Klient</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data dostawy</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Akcje</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {wzDocuments.map((doc) => (
                        <tr key={doc.id} className="hover:bg-gray-800">
                          <td className="px-4 py-3 text-sm font-medium text-white">
                            {doc.document_number}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {doc.client_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {new Date(doc.delivery_date).toLocaleDateString('pl-PL')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              doc.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                              doc.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {doc.status === 'sent' ? 'Wysłany' :
                               doc.status === 'confirmed' ? 'Zatwierdzony' : 'Szkic'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => deleteWZDocument(doc.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Usuń"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsWZManagementOpen(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog zarządzania kontrolami jakości */}
      {isQCManagementOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-75" onClick={() => setIsQCManagementOpen(false)} />
            
            <div className="relative bg-gray-800 rounded-lg max-w-full lg:max-w-5xl w-full p-4 md:p-6 max-h-[85vh] overflow-auto">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Zarządzanie kontrolami jakości</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Znaleziono {qcChecks.length} kontroli
                  </p>
                </div>
                <button
                  onClick={() => setIsQCManagementOpen(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {qcLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : qcChecks.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Brak kontroli jakości w systemie</p>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-lg overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data kontroli</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Produkt</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Zamówienie</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Inspektor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">Akcje</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {qcChecks.map((check) => (
                        <tr key={check.id} className="hover:bg-gray-800">
                          <td className="px-4 py-3 text-sm text-white">
                            {new Date(check.check_date).toLocaleDateString('pl-PL')}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {check.product_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {check.order_number || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {check.inspector}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              check.status === 'passed' ? 'bg-green-500/20 text-green-400' :
                              check.status === 'conditional' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {check.status === 'passed' ? 'Zgodne' :
                               check.status === 'conditional' ? 'Warunkowe' : 'Niezgodne'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => deleteQCCheck(check.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Usuń"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsQCManagementOpen(false)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog dodawania/edycji użytkownika */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-75" onClick={() => setIsDialogOpen(false)} />
            
            <div className="relative bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <h2 className="text-xl font-bold text-white mb-2">
                {editingUser ? 'Edytuj użytkownika' : 'Dodaj nowego użytkownika'}
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                {editingUser 
                  ? 'Zaktualizuj dane użytkownika i jego uprawnienia'
                  : 'Dodaj adres email pracownika. Po pierwszym logowaniu przez Google, konto zostanie automatycznie aktywowane.'
                }
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                    disabled={!!editingUser}
                    placeholder="pracownik@firma.pl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Imię i nazwisko
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
                    placeholder="Jan Kowalski"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Rola
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  >
                    {ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Moduły
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {AVAILABLE_MODULES.map((module) => (
                      <div
                        key={module.value}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          formData.modules_access.includes(module.value)
                            ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                            : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        }`}
                        onClick={() => toggleModule(module.value)}
                      >
                        <div className="flex items-center gap-2">
                          <span>{module.icon}</span>
                          <span className="text-sm font-medium">{module.label}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsDialogOpen(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                  disabled={saveLoading}
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={saveLoading}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saveLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                  {editingUser ? 'Zapisz zmiany' : 'Dodaj użytkownika'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;